/**
 * The site background the owner picks in the admin: two colours and an angle, or the built-in look.
 * Dark mode shows the gradient; light mode shows a pale wash of the same two colours, so text stays readable in both.
 */
export interface Background { mode: "default" | "custom"; c1?: string; c2?: string; angle?: number }

const HEX = /^#[0-9a-f]{6}$/i;
export const isCustom = (b?: Background | null): b is Required<Background> => !!b && b.mode === "custom" && HEX.test(b.c1 ?? "") && HEX.test(b.c2 ?? "");

/** The CSS that sets `--bg-fill` for both themes. Empty for the default look. Only validated colours reach it. */
export function backgroundCss(b?: Background | null): string {
  if (!isCustom(b)) return "";
  const a = Math.min(360, Math.max(0, Math.round(b.angle ?? 160)));
  const dark = `linear-gradient(${a}deg, ${b.c1}, ${b.c2})`;
  const glow = `radial-gradient(800px 600px at 85% -10%, ${b.c2}, transparent 65%)`;
  const light = `radial-gradient(700px 500px at 100% 0%, rgba(255,255,255,.85), transparent 60%), linear-gradient(${a}deg, color-mix(in srgb, ${b.c1} 22%, white), color-mix(in srgb, ${b.c2} 22%, white))`;
  const D = `--bg-fill: ${glow}, ${dark};`, L = `--bg-fill: ${light};`;
  return `:root[data-theme="dark"] { ${D} }
@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${D} } }
:root[data-theme="light"] { ${L} }
@media (prefers-color-scheme: light) { :root:not([data-theme="dark"]) { ${L} } }`;
}

/** Puts the background on the page. Safe to call again; null puts the default back. */
export function applyBackground(b?: Background | null, doc: Document = document) {
  let el = doc.getElementById("hg-bg") as HTMLStyleElement | null;
  const css = backgroundCss(b);
  if (!css) { el?.remove(); return; }
  if (!el) { el = doc.createElement("style"); el.id = "hg-bg"; doc.head.appendChild(el); }
  el.textContent = css;
}

const hex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
function hsl(h: number, s: number, l: number): string {
  const k = (n: number) => (n + h / 30) % 12, a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${hex(f(0) * 255)}${hex(f(8) * 255)}${hex(f(4) * 255)}`;
}

/** A random dark gradient: a deep, calm colour with a second one close by on the colour wheel. */
export function randomDarkGradient(rand: () => number = Math.random): Required<Background> {
  const h = rand() * 360, h2 = (h + 30 + rand() * 70) % 360;
  return { mode: "custom", c1: hsl(h, 0.45 + rand() * 0.3, 0.06 + rand() * 0.05), c2: hsl(h2, 0.5 + rand() * 0.3, 0.13 + rand() * 0.07), angle: Math.round(rand() * 360) };
}
